import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import {
  Col,
  Container,
  Row,
  Table,
  Modal,
  Form,
} from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import { Button, Pagination } from "../../components/ui";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import warehouseIcon from "../../media/icons8-hierarchy.png";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import filterIcon from "../../media/filter_icon.png";

function Warehouse() {
  const { t } = useTranslation("warehouse");
  const [items, setItems] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [selectedClinic, setSelectedClinic] = useState("all");
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  // Модальное окно добавления/редактирования склада
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [warehouseForm, setWarehouseForm] = useState({
    name: "",
    id_clinic: "",
  });

  useEffect(() => {
    document.title = t("list.title");
  }, [t]);

  const loadWarehouse = async () => {
    const preserveSearchFocus = captureSearchFocus();
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "warehouse",
        block: "list",
        search: debouncedSearchTerm.trim(),
        id_clinic: selectedClinic === "all" ? "" : selectedClinic,
        page: pagination.page,
        limit: pagination.limit,
      });

      const result = data.result || data.request?.result || [];
      const clinicsData = data.clinics || data.request?.clinics || [];
      const paginationData =
        data.pagination || data.request?.pagination || null;

      setItems(result);
      setClinics(clinicsData);

      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          page: paginationData.page ?? prev.page,
          limit: paginationData.limit ?? prev.limit,
          total: paginationData.total ?? prev.total,
          pages: paginationData.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      console.error("Ошибка загрузки склада:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  };

  useEffect(() => {
    loadWarehouse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedClinic, pagination.page]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClinicChange = (e) => {
    setSelectedClinic(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setWarehouseForm((prev) => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    const defaultClinicId =
      selectedClinic !== "all" && selectedClinic
        ? selectedClinic
        : clinics[0]?.id?.value || clinics[0]?.id || "";

    setEditingWarehouse(null);
    setWarehouseForm({
      name: "",
      id_clinic: String(defaultClinicId ?? ""),
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    const idValue = item?.id?.value || item?.id;
    const clinicValue = item?.id_clinic?.value || item?.id_clinic || "";

    setEditingWarehouse({
      id: idValue,
    });
    setWarehouseForm({
      name: item?.name?.value || item?.name || "",
      id_clinic: String(clinicValue ?? ""),
    });
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleSaveWarehouse = async () => {
    if (!warehouseForm.name.trim() || !warehouseForm.id_clinic) {
      // Простая валидация
      // eslint-disable-next-line no-alert
      alert(t("list.errors.needNameAndClinic"));
      return;
    }

    try {
      await GetData({
        module: "warehouse",
        block: "list",
        action: editingWarehouse ? "edit" : "add",
        id: editingWarehouse?.id,
        name: warehouseForm.name.trim(),
        id_clinic: warehouseForm.id_clinic,
      });
      await loadWarehouse();
      setShowModal(false);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(t("list.errors.saveFailed"));
      // eslint-disable-next-line no-console
      console.error("Ошибка сохранения склада:", err);
    }
  };

  // Есть ли выбранный фильтр: подсвечиваем такие поля, кнопки «Сбросить» больше нет.
  const hasActiveFilters =
    searchTerm.trim() !== "" || selectedClinic !== "all";

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const renderPageButtons = () => (
    <Pagination
      onChange={handlePageClick}
      page={pagination.page}
      totalPages={totalPages}
    />
  );

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={warehouseIcon}
                title={t("list.title")}
                showStatus={false}
                status=""
              />
            }
            buttons={[
              {
                label: t("list.add"),
                onClick: openAddModal,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              {/* Панель фильтров */}
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""} ${
                    hasActiveFilters ? "has-active-filters" : ""
                  }`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <Button
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                      size="sm"
                      variant="secondary">
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </Button>
                  </div>
                  <div className="search-section">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`search-input ${searchTerm.trim() ? "is-filter-active" : ""}`}
                    />
                  </div>
                  <div className="filters-section">
                    <div
                      className={`filter-group ${selectedClinic !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          selectedClinic === "all" ? "is-placeholder" : ""
                        }
                        value={selectedClinic}
                        onChange={handleClinicChange}
                      >
                        <option value="all">
                          {asPlaceholder(t("list.clinicLabel"))}
                        </option>
                        {clinics.map((clinic) => (
                          <option
                            key={clinic?.id?.value || clinic?.id}
                            value={clinic?.id?.value || clinic?.id}
                          >
                            {clinic?.name?.value || clinic?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Таблица склада */}
              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : items.length === 0 ? (
                  <div className="no-patients">{t("list.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colName")}</th>
                        <th>{t("list.colClinic")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={item?.id?.value || item?.id}
                          className="table-item-link"
                          onClick={() => openEditModal(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{item?.name?.value || item?.name}</td>
                          <td>
                            {item?.clinic_name?.value ||
                              item?.clinic_name ||
                              ""}
                          </td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={2} className="summary-cell">
                          {t("shared.foundItems", { count: pagination.total || items.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {items.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("shared.pageOf", { page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Модальное окно добавления/редактирования склада */}
      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingWarehouse ? t("list.editTitle") : t("list.addTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("list.modalClinic")}</Form.Label>
              <Form.Select
                name="id_clinic"
                value={warehouseForm.id_clinic}
                onChange={handleFormChange}
              >
                <option value="">{t("list.selectClinic")}</option>
                {clinics.map((clinic) => (
                  <option
                    key={clinic?.id?.value || clinic?.id}
                    value={clinic?.id?.value || clinic?.id}
                  >
                    {clinic?.name?.value || clinic?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("list.modalName")}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={warehouseForm.name}
                onChange={handleFormChange}
                placeholder={t("list.namePlaceholder")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            {t("actions.cancel", { ns: "common" })}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveWarehouse}
            disabled={!warehouseForm.name.trim()}
          >
            {t("actions.save", { ns: "common" })}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default Warehouse;
